import { Component, OnInit, OnDestroy } from '@angular/core';
import {
  AppMasterDBFacade,
  NetworkStorageDbService,
  ActivationService,
  FactoryService,
  AppShellScheduler,
} from '@monorepo/core-data';
import { SplashScreen } from '@capacitor/splash-screen';
import { Platform } from '@ionic/angular';
import { LOCAL_STORAGE_UTILS } from '@monorepo/utils';
import {
  CapacitorHttpService,
  CustomLoggerService,
  GlobalUnhandleAngularErrorLogService,
  GlobalUnhandledJSErrorLogService,
} from '@monorepo/services';
import { TAppDetailsWithAdditionalInfo, TInputAppDeviceInfo, AppConfig } from '@monorepo/types';
import { TranslateService } from '@ngx-translate/core';
import { ErrorHandlerService } from './app.module';
import { Subscription } from 'rxjs';

@Component({
  selector: 'monorepo-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];

  constructor(
    private networkStorageService: NetworkStorageDbService,
    private platformSvc: Platform,
    private capacitorHttpService: CapacitorHttpService,
    private translate: TranslateService,
    private customLoggerService: CustomLoggerService<TInputAppDeviceInfo>,
    private errorHandlerService: ErrorHandlerService,
    private globalUnhandleJSErrorLogService: GlobalUnhandledJSErrorLogService,
    private factoryService: FactoryService,
    private activationService: ActivationService,
    private appMasterDBFacade: AppMasterDBFacade,
    private appShellScheduler: AppShellScheduler,
  ) {
    this.platformSvc.ready().then(async () => {
      await SplashScreen.hide();
    });
    this.initializeLocale();
    this.capacitorHttpService.init();

    // Set up the error handler after services are initialized
    const errorHandler = new GlobalUnhandleAngularErrorLogService(this.customLoggerService);
    this.errorHandlerService.setErrorHandler(errorHandler);
    this.globalUnhandleJSErrorLogService.initGlobalErrorHandlers();

    const sub = this.appMasterDBFacade.getAppDetailsForLogger().subscribe((res: TAppDetailsWithAdditionalInfo) => {
      const deviceAppInfo: TInputAppDeviceInfo = {
        factoryId: res.factoryId,
        appType: 'input-app',
        appVersion: res.appVersion,
        shift: res.shift,
        subject: res.subject,
        appName: res.an,
        deviceId: this.activationService.getDeviceId(),
      };
      const config = { logEndpoint: this.factoryService.getBulkLoggingAPI() } as AppConfig;
      this.customLoggerService.initializeLogger(config, deviceAppInfo);
      // Started here rather than in a sub-app shell so every app identifier reports device
      // stats. The logger must be initialised first, otherwise it rejects the log.
      this.appShellScheduler.logDeviceStatsPeriodically();
    });
    this.subscriptions.push(sub);
  }

  async ngOnInit() {
    await this.networkStorageService.init();
    await this.#askPersistantStoragePermission();
  }

  async initializeLocale() {
    const locale = LOCAL_STORAGE_UTILS.retrieveData('locale');
    if (locale) {
      this.translate.setDefaultLang(locale);
      const cookieVal = await this.capacitorHttpService.getCookie('locale');
      if (locale !== cookieVal) {
        this.capacitorHttpService.setCookie({ key: 'locale', value: locale });
      }
    } else {
      this.translate.setDefaultLang('en');
      LOCAL_STORAGE_UTILS.storeData('locale', 'en');
      this.capacitorHttpService.setCookie({ key: 'locale', value: 'en' });
    }
  }

  async #askPersistantStoragePermission() {
    try {
      const isPersisted = await navigator.storage.persisted();

      if (!isPersisted) {
        const isSuccess = await navigator.storage.persist();
        if (isSuccess)
          this.customLoggerService.log('[PERSIST_STORAGE_PERMISSION]', 'Storage successfully marked as persistent.');
        else this.customLoggerService.log('[PERSIST_STORAGE_PERMISSION]', 'Storage persistence request was denied.');
      } else {
        this.customLoggerService.log('[PERSIST_STORAGE_PERMISSION]', 'Storage is already persistent.');
      }
    } catch (error) {
      this.customLoggerService.error(
        '[PERSIST_STORAGE_PERMISSION]',
        'Error while requesting persistent storage',
        error,
      );
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
